import { useCallback, useContext, useRef, useState } from 'react';
import GlobalContext from '@/context/GlobalContext';
import { supabase } from '@/lib/supabase';

export interface KbSource {
	id: string;
	title: string;
	url: string;
}

export interface ChatMessage {
	role: 'user' | 'assistant';
	content: string;
	sources?: KbSource[];
}

interface UseAskElevateReturn {
	messages: ChatMessage[];
	ask: (question: string) => Promise<void>;
	loading: boolean;
	streaming: boolean;
	error: string | null;
	reset: () => void;
}

const FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/kb-assistant`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const SOURCES_MARKER = '\n__KB_SOURCES__';

export const useAskElevate = (): UseAskElevateReturn => {
	const { currentUser } = useContext(GlobalContext);
	const [messages, setMessages] = useState<ChatMessage[]>([]);
	const [loading, setLoading] = useState(false);
	const [streaming, setStreaming] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const inFlight = useRef(false);

	const ask = useCallback(
		async (question: string) => {
			const trimmed = question.trim();
			if (!trimmed || inFlight.current) return;

			inFlight.current = true;
			setError(null);
			setLoading(true);
			setStreaming(false);

			const history = messages.map(({ role, content }) => ({ role, content }));
			setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);

			// Updates the content of the streaming assistant message (always the last one).
			const setAssistant = (content: string, sources?: KbSource[]) => {
				setMessages((prev) => {
					const next = [...prev];
					const last = next[next.length - 1];
					if (last?.role === 'assistant') {
						next[next.length - 1] = { ...last, content, ...(sources ? { sources } : {}) };
					}
					return next;
				});
			};

			try {
				const {
					data: { session },
				} = await supabase.auth.getSession();
				const token = session?.access_token ?? ANON_KEY;

				const response = await fetch(FUNCTION_URL, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						apikey: ANON_KEY,
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify({
						question: trimmed,
						history,
						schoolId: currentUser?.school_id,
						userId: currentUser?.user_id,
					}),
				});

				if (!response.ok || !response.body) {
					throw new Error(`Request failed (${response.status})`);
				}

				const reader = response.body.getReader();
				const decoder = new TextDecoder();
				let acc = '';
				let assistantStarted = false;

				while (true) {
					const { done, value } = await reader.read();
					if (done) break;
					acc += decoder.decode(value, { stream: true });

					const markerIdx = acc.indexOf(SOURCES_MARKER);
					const answerText = markerIdx === -1 ? acc : acc.slice(0, markerIdx);

					if (!assistantStarted && answerText) {
						assistantStarted = true;
						setLoading(false);
						setStreaming(true);
						setMessages((prev) => [
							...prev,
							{ role: 'assistant', content: answerText, sources: [] },
						]);
					} else if (assistantStarted) {
						setAssistant(answerText);
					}
				}

				// Parse trailing sources frame, if present.
				const markerIdx = acc.indexOf(SOURCES_MARKER);
				const answerText = markerIdx === -1 ? acc : acc.slice(0, markerIdx);
				let sources: KbSource[] = [];
				if (markerIdx !== -1) {
					try {
						sources = JSON.parse(acc.slice(markerIdx + SOURCES_MARKER.length).trim());
					} catch {
						sources = [];
					}
				}

				if (!assistantStarted) {
					// Stream produced no text before completing — surface whatever we have.
					setMessages((prev) => [
						...prev,
						{ role: 'assistant', content: answerText || '…', sources },
					]);
				} else {
					setAssistant(answerText, sources);
				}
			} catch (err) {
				console.error('useAskElevate error:', err);
				setError('Something went wrong. Please try again.');
			} finally {
				setLoading(false);
				setStreaming(false);
				inFlight.current = false;
			}
		},
		[messages, currentUser]
	);

	const reset = useCallback(() => {
		setMessages([]);
		setError(null);
	}, []);

	return { messages, ask, loading, streaming, error, reset };
};

export default useAskElevate;
